import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import { Button } from './ui/Button';

type Eligibility = {
  eligible: boolean;
  reasons: string[];
  checks?: Record<string, unknown>;
  completeness?: { percent?: number };
};

type CertBundle = {
  vehicle: {
    certification_status: string;
    is_certified?: boolean;
    certified_at?: string | null;
  } | null;
  eligibility: Eligibility;
  latest_request: {
    id: number;
    status: string;
    statement: string;
    admin_notes?: string | null;
    created_at?: string | null;
  } | null;
};

type Props = {
  vehicleId: number;
  isPublic: boolean;
};

export const VehicleCertificationPanel: React.FC<Props> = ({ vehicleId, isPublic }) => {
  const [bundle, setBundle] = useState<CertBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statement, setStatement] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getVehicleCertification(vehicleId);
      const data = (res as { data?: CertBundle }).data || null;
      setBundle(data);
      if (data?.latest_request?.statement && data.vehicle?.certification_status === 'pending_review') {
        setStatement(data.latest_request.statement);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load certification status');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    void load();
  }, [load]);

  const status = bundle?.vehicle?.certification_status || 'unverified';
  const eligibility = bundle?.eligibility;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiService.submitVehicleCertification(vehicleId, {
        statement: statement.trim(),
        notes: notes.trim() || undefined,
      });
      await load();
    } catch (e: unknown) {
      const err = e as Error & { reasons?: string[] };
      setError(
        err.reasons?.length
          ? err.reasons.join('. ')
          : err.message || 'Could not submit certification request'
      );
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiService.withdrawVehicleCertification(vehicleId);
      setStatement('');
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not withdraw request');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-4">
        <p className="text-sm text-gray-400">Loading certification…</p>
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-3">
      <div>
        <h3 className="text-lg font-semibold text-white">Bilt Certified</h3>
        <p className="text-sm text-gray-400 mt-1">
          Staff review that your public build page looks honest. Photos and listed mods checked.
          Not a valuation or title search.
        </p>
      </div>

      <p className="text-sm text-gray-300">
        Status:{' '}
        <span className="text-white font-medium">
          {status === 'certified'
            ? 'Certified'
            : status === 'pending_review'
              ? 'In review'
              : status === 'rejected'
                ? 'Rejected'
                : status === 'revoked'
                  ? 'Revoked'
                  : 'Not certified'}
        </span>
        {bundle?.vehicle?.certified_at && status === 'certified'
          ? ` · since ${bundle.vehicle.certified_at}`
          : null}
      </p>

      {bundle?.latest_request?.admin_notes &&
      (status === 'rejected' || status === 'pending_review' || status === 'revoked') ? (
        <p className="text-sm text-amber-200/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-2">
          Staff note: {bundle.latest_request.admin_notes}
        </p>
      ) : null}

      {!isPublic ? (
        <p className="text-sm text-amber-300/90">Make this vehicle public before requesting certification.</p>
      ) : null}

      {status === 'pending_review' ? (
        <div className="space-y-2">
          <p className="text-sm text-gray-400">
            Your request is in the review queue. You can withdraw it anytime before a decision.
          </p>
          <Button type="button" variant="ghost" disabled={saving} onClick={() => void withdraw()}>
            Withdraw request
          </Button>
        </div>
      ) : null}

      {status === 'certified' ? (
        <p className="text-sm text-emerald-300/90">
          This build shows the Certified seal on Discover and public cards.
        </p>
      ) : null}

      {(status === 'unverified' || status === 'rejected' || status === 'revoked') && isPublic ? (
        <div className="space-y-3">
          {eligibility && !eligibility.eligible ? (
            <ul className="text-sm text-amber-200/90 list-disc pl-5 space-y-1">
              {eligibility.reasons.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          ) : null}
          <label className="block text-sm text-gray-300">
            Owner statement
            <textarea
              className="mt-1 w-full rounded-lg bg-[#0a0a0a] border border-white/[0.06] px-3 py-2 text-white min-h-[96px]"
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              placeholder="Confirm this page accurately represents the car (min 20 characters)."
              maxLength={2000}
            />
          </label>
          <label className="block text-sm text-gray-300">
            Optional notes
            <textarea
              className="mt-1 w-full rounded-lg bg-[#0a0a0a] border border-white/[0.06] px-3 py-2 text-white min-h-[64px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Restomod, tribute, unfinished areas, etc."
              maxLength={1000}
            />
          </label>
          <Button
            type="button"
            variant="primary"
            disabled={saving || !eligibility?.eligible || statement.trim().length < 20}
            onClick={() => void submit()}
          >
            Request certification
          </Button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default VehicleCertificationPanel;
